import type {
  CreateThreatModelInput,
  ThreatModelRevisionView,
  ThreatModelWorkspace,
} from "../../api/types.js";

export type ThreatModelDraftElement = CreateThreatModelInput["elements"][number];

export interface ThreatModelDraft {
  title: string;
  elements: ThreatModelDraftElement[];
  assumptionsText: string;
  expectedParentSha256: string | null;
}

function withoutEvidence(revision: ThreatModelRevisionView): ThreatModelDraftElement[] {
  return revision.elements.map(({ evidence: _evidence, ...element }) => ({
    ...element,
    nodeIds: [...element.nodeIds],
    edgeIds: [...element.edgeIds],
  }));
}

export function draftFromWorkspace(workspace: ThreatModelWorkspace): ThreatModelDraft {
  const latest = workspace.revisions.at(-1);
  return {
    title: latest?.title ?? "",
    elements: latest ? withoutEvidence(latest) : [],
    assumptionsText: latest?.assumptions.join("\n") ?? "",
    expectedParentSha256: latest?.canonicalSha256 ?? null,
  };
}

export function workspaceRevisionKey(workspace: ThreatModelWorkspace): string {
  return `${workspace.projection.graph.snapshotId}:${workspace.revisions.at(-1)?.canonicalSha256 ?? "initial"}`;
}

export function elementValidationError(
  element: ThreatModelDraftElement,
  workspace: ThreatModelWorkspace,
): string | null {
  if (!element.title.trim() || !element.description.trim()) {
    return "Title and description are required.";
  }
  const nodes = new Set(workspace.projection.graph.nodes.map((node) => node.id));
  const edges = new Set(workspace.projection.graph.edges.map((edge) => edge.id));
  if (
    element.nodeIds.some((id) => !nodes.has(id)) ||
    element.edgeIds.some((id) => !edges.has(id))
  ) {
    return "A selected graph fact is no longer part of this frozen projection.";
  }
  if (element.nodeIds.length + element.edgeIds.length === 0) {
    return "Select at least one evidence-observed node or validated edge.";
  }
  if (element.kind === "data_flow" && element.edgeIds.length === 0) {
    return "A data flow requires at least one validated edge.";
  }
  if (
    element.kind === "trust_boundary" &&
    element.edgeIds.length === 0 &&
    element.nodeIds.length < 2
  ) {
    return "A trust boundary requires a validated edge or at least two observed nodes.";
  }
  return null;
}

export function threatModelPayload(
  snapshotId: string,
  draft: ThreatModelDraft,
  changeReason: string,
): CreateThreatModelInput {
  const assumptions = [
    ...new Set(
      draft.assumptionsText
        .split("\n")
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  ];
  return {
    snapshotId,
    expectedParentSha256: draft.expectedParentSha256,
    title: draft.title.trim(),
    elements: draft.elements.map((element) => ({
      ...element,
      title: element.title.trim(),
      description: element.description.trim(),
      nodeIds: [...new Set(element.nodeIds)].sort(),
      edgeIds: [...new Set(element.edgeIds)].sort(),
    })),
    assumptions,
    changeReason: changeReason.trim(),
  };
}
